/**
 * SoundBank — the cue registry + the EventName→cue binding table (L4).
 *
 * "What happened" (an event) is decoupled here from "what it sounds like" (a cue),
 * so adding a sound for a new event is one table row, not a scattered `bus.on(...)`.
 * Pure data + lookups — the AudioEngine consumes this and owns the actual subscriptions
 * (where the typed EventBus checks the event names). Architecture §4.
 */

import type { Cue } from '../engine/audioTypes';
import { CUES as GENERATED_CUES, BINDINGS as GENERATED_BINDINGS } from './cues.generated';
import { ABILITY_FAMILY_CUES } from './abilityFamily.cues';
import { HUSH_CUES, HUSH_EVENT_CUES } from './hush.cues';

/** Authored (cue.toml) cues + the ability sound families (plan 29 F3), which
 *  are built from the content vocabulary rather than authored one by one. */
const ALL_CUES: Cue[] = [...GENERATED_CUES, ...ABILITY_FAMILY_CUES, ...HUSH_CUES];

const CUE_BY_ID: Map<string, Cue> = new Map(ALL_CUES.map((c) => [c.id, c]));

/**
 * Explicit process-wide cue overrides. The gameplay sampled bank deliberately
 * does not use this map: WasmAudioEngine keeps its sample descriptors
 * instance-local so an isolated debug engine cannot change another engine's
 * synth fallback. Retain the seam for deliberate runtime authoring/debug use.
 */
const OVERRIDE_CUES: Map<string, Cue> = new Map();

/** Register an explicit process-wide runtime override. */
export function registerCue(cue: Cue): void {
  OVERRIDE_CUES.set(cue.id, cue);
}

/** Remove a runtime override (restores the static cue, if any). */
export function unregisterCue(cueId: string): void {
  OVERRIDE_CUES.delete(cueId);
}

export function resolveCue(id: string): Cue | undefined {
  return OVERRIDE_CUES.get(id) ?? CUE_BY_ID.get(id);
}

export function allCueIds(): string[] {
  return [...CUE_BY_ID.keys()];
}

/** Every authored cue — used by the wasm engine to build its CueTable. */
export function allCues(): Cue[] {
  return [...ALL_CUES];
}

/**
 * Static (typed) EventBus events → cue id. Derived from the codegen-emitted
 * BINDINGS table (cue.toml is the source of truth). The AudioEngine subscribes
 * each via the typed `bus.on(name)`, so the names are validated as EventName
 * at that call site.
 */
export const STATIC_EVENT_CUES: Record<string, string> = Object.fromEntries(
  GENERATED_BINDINGS
    .filter((b) => b.event !== 'ability:cast') // ability:cast is dynamic (see below)
    .map((b) => [b.event, b.cues[0]]),
);

/** Dynamic (string-bus) events → cue id, subscribed via `bus.onDynamic(...)`.
 *
 * `ability:cast` is retained here only because cue.toml still exports that
 * historical binding. It has no emitter. Live cast audio uses the typed
 * AUDIO_ABILITY_CAST (local) and ABILITY_CASTED (remote) bridges in
 * GameAudioSystem; reviving this dynamic event would also wake a legacy VFX
 * listener and duplicate authored cast presentation.
 */
export const DYNAMIC_EVENT_CUES: Record<string, string> = Object.fromEntries(
  GENERATED_BINDINGS
    .filter((b) => b.event === 'ability:cast')
    .map((b) => [b.event, b.cues[0]]),
);

/** In-repo world-event bindings (not from cue.toml), typed events subscribed beside
 *  the static table. Kept separate so the cue.toml parity check stays exact. */
export const WORLD_EVENT_CUES: Record<string, string> = { ...HUSH_EVENT_CUES };

// GM Race (A.6): race event→cue bindings are payload-dependent (hazard kind,
// finish place, final-3 tick), which the flat tables above can't select — they
// live as selector functions in race.cues.ts (RACE_CUE_BINDINGS), subscribed
// by RaceAudioSystem. That selector delegates every fire to GameAudioSystem;
// it is not a second engine/device owner.

/** Every cue id referenced by a binding — used by the parity test to catch typos/orphans. */
export function boundCueIds(): string[] {
  return [...Object.values(STATIC_EVENT_CUES), ...Object.values(DYNAMIC_EVENT_CUES), ...Object.values(WORLD_EVENT_CUES)];
}
